import { useEffect, useMemo, useState } from "react";
import { BadgeCheck, FileCode2, Plug, Search, Users, Wrench } from "lucide-react";
import { Badge } from "../ui/Badge";
import { Spinner } from "../ui/Spinner";
import { Input } from "../ui/Input";
import { listExchange } from "../../lib/api";
import type { ExchangeListing, ExchangeScope } from "../../lib/api";
import { SystemLogo } from "./SystemLogo";
import { cn } from "../../lib/cn";

/** Pick a published connector from the exchange.
 *
 * Shared by the "New integration" flow and (later) the workflow builder, so
 * choosing an existing connector looks and behaves the same everywhere.
 * Selection only — the caller decides what to do with the listing.
 */
export function ConnectorPicker({
  value,
  onSelect,
  scope: initialScope = "official",
  compact = false,
}: {
  value?: ExchangeListing | null;
  onSelect: (listing: ExchangeListing) => void;
  scope?: ExchangeScope;
  compact?: boolean;
}) {
  const [listings, setListings] = useState<ExchangeListing[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<ExchangeScope>(initialScope);
  const [q, setQ] = useState("");

  useEffect(() => {
    let live = true;
    setListings(null);
    setError(null);
    listExchange("all")
      .then((rows) => live && setListings(rows))
      .catch(() => live && setError("Couldn't load the exchange."));
    return () => {
      live = false;
    };
  }, []);

  const counts = useMemo(
    () => ({
      official: listings?.filter((l) => l.is_official).length ?? 0,
      community: listings?.filter((l) => !l.is_official).length ?? 0,
      all: listings?.length ?? 0,
    }),
    [listings],
  );

  const shown = useMemo(() => {
    if (!listings) return [];
    const inScope = listings.filter((l) =>
      scope === "all" ? true : scope === "official" ? l.is_official : !l.is_official,
    );
    const term = q.trim().toLowerCase();
    if (!term) return inScope;
    return inScope.filter(
      (l) =>
        l.display_name.toLowerCase().includes(term) ||
        l.system_type.toLowerCase().includes(term) ||
        l.description.toLowerCase().includes(term),
    );
  }, [listings, q, scope]);

  const TABS: { scope: ExchangeScope; label: string }[] = [
    { scope: "official", label: "Wise Exchange" },
    { scope: "community", label: "From workspaces" },
    { scope: "all", label: "All" },
  ];

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-1.5">
          {TABS.map((t) => {
            const active = scope === t.scope;
            return (
              <button
                key={t.scope}
                type="button"
                onClick={() => setScope(t.scope)}
                className={cn(
                  "focus-ring inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition",
                  active
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700",
                )}
              >
                {t.scope === "official" ? (
                  <BadgeCheck className="h-3.5 w-3.5" />
                ) : t.scope === "community" ? (
                  <Users className="h-3.5 w-3.5" />
                ) : null}
                {t.label}
                <span className={active ? "text-white/60" : "text-slate-400"}>
                  {counts[t.scope]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative w-full sm:w-56">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search connectors…"
            className="h-9 pl-9 text-sm"
          />
        </div>
      </div>

      <div className={cn("mt-4 overflow-y-auto pr-1", compact ? "max-h-64" : "max-h-[26rem]")}>
        {listings === null ? (
          <div className="flex justify-center py-10">
            <Spinner className="h-6 w-6 text-brand-500" />
          </div>
        ) : error ? (
          <p className="py-10 text-center text-sm text-slate-400">{error}</p>
        ) : shown.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">
            {q
              ? "No connectors match your search."
              : scope === "official"
                ? "The Wise Exchange has no connectors yet."
                : "No workspace has published a connector yet."}
          </p>
        ) : (
          <ul className="grid gap-2.5 sm:grid-cols-2">
            {shown.map((l) => {
              const selected = value?.id === l.id;
              return (
                <li key={l.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(l)}
                    className={cn(
                      "focus-ring flex w-full items-start gap-3 rounded-xl border p-3.5 text-left transition",
                      selected
                        ? "border-brand-400 bg-brand-50/60 ring-1 ring-brand-200"
                        : "border-slate-200 bg-white hover:border-brand-200 hover:bg-slate-50",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1",
                        l.is_official
                          ? "bg-white ring-slate-200"
                          : "bg-brand-gradient text-white ring-transparent",
                      )}
                    >
                      {l.is_official ? (
                        <SystemLogo
                          iconSlug={l.icon_slug}
                          systemType={l.system_type}
                          brandColor={l.brand_color}
                          name={l.display_name}
                          size={22}
                        />
                      ) : (
                        <Plug className="h-5 w-5" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-semibold text-slate-900">
                          {l.display_name}
                        </span>
                        {l.is_official && (
                          <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-brand-500" />
                        )}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-slate-400">
                        {l.system_type} · {l.publisher_name || "unknown"}
                      </span>
                      <span className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                        <span className="inline-flex items-center gap-1">
                          {l.has_code ? (
                            <FileCode2 className="h-3 w-3" />
                          ) : (
                            <Wrench className="h-3 w-3" />
                          )}
                          {l.has_code ? "with code" : "schema only"}
                        </span>
                        <span>{l.params_schema.length} params</span>
                        {l.capabilities.length > 0 && (
                          <Badge tone="brand" className="font-mono text-[10px]">
                            {l.capabilities.length} ops
                          </Badge>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
